import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, LockKey } from "@phosphor-icons/react";

export default function ResetPassword() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Ensure we have a valid recovery session from Supabase
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.includes("type=recovery")) {
      // No recovery token in URL — user arrived here directly
      toast.error("Invalid or expired reset link");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setLoading(true);
    const res = await updatePassword(password);
    setLoading(false);

    if (res.ok) {
      setDone(true);
      toast.success("Password updated successfully!");
      // Redirect to login after a short delay
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-white"
               style={{ background: "var(--primary)", border: "1.5px solid var(--ink)", boxShadow: "2px 2px 0 0 var(--ink)" }}>
            RA
          </div>
          <span className="display font-black text-2xl">Rating Ally</span>
        </Link>

        <div className="card-pop p-7 sm:p-9">
          <h1 className="display text-3xl font-black tracking-tighter">New password</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-1">Create a strong password for your account.</p>

          {done ? (
            <div className="mt-7 text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 text-emerald-600 mb-4">
                <LockKey size={32} weight="bold" />
              </div>
              <p className="text-[var(--ink)] font-medium">Password updated!</p>
              <p className="text-sm text-[var(--ink-muted)] mt-1">Redirecting you to sign in...</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">New password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  className="input-pop mt-1.5"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Confirm password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  className="input-pop mt-1.5"
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <button disabled={loading} type="submit" className="btn-primary w-full justify-center mt-2">
                {loading ? "Updating…" : "Update password"}
              </button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)] inline-flex items-center gap-1">
                  <ArrowLeft size={14} /> Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}